import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Search, Menu, X, User, LogOut, Wallet, ChevronDown, ShieldAlert, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";

const CATEGORIES = [
  { slug: "gundem", emoji: "🔥", tr: "Gündem", en: "Breaking" },
  { slug: "ekonomi", emoji: "📈", tr: "Ekonomi", en: "Economy" },
  { slug: "spor", emoji: "⚽", tr: "Spor", en: "Sports" },
  { slug: "teknoloji", emoji: "💻", tr: "Teknoloji", en: "Technology" },
  { slug: "dunya", emoji: "🌍", tr: "Dünya", en: "World" },
  { slug: "saglik", emoji: "🏥", tr: "Sağlık", en: "Health" },
];

export default function Header() {
  const { lang, setLang, t } = useLang();
  const { user, logout } = useAuth();
  const [q, setQ] = useState("");
  const [now, setNow] = useState(new Date());
  const [noticeDismissed, setNoticeDismissed] = useState(() => {
    try { return localStorage.getItem("loft_api_scope_notice_dismissed") === "1"; }
    catch { return false; }
  });
  const dismissNotice = (e) => {
    e.preventDefault(); e.stopPropagation();
    try { localStorage.setItem("loft_api_scope_notice_dismissed", "1"); } catch { /* ignore */ }
    setNoticeDismissed(true);
  };
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const close = () => setUserMenuOpen(false);
    if (userMenuOpen) {
      window.addEventListener("click", close);
      return () => window.removeEventListener("click", close);
    }
  }, [userMenuOpen]);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
      setMobileOpen(false);
    }
  };

  const dateLabel = now.toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <header className="sticky top-0 z-40 bg-black/95 backdrop-blur-xl border-b border-neutral-800" data-testid="site-header">
      {/* Top utility bar */}
      <div className="border-b border-neutral-800 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between text-xs text-neutral-400">
          <span data-testid="header-date" className="font-mono uppercase tracking-wider">{dateLabel}</span>
          <div className="flex items-center gap-4">
            <Link to="/marketplace" className="hover:text-red-500 transition-colors" data-testid="header-marketplace-link">
              {t("nav_marketplace")}
            </Link>
            <Link to="/marketplace/docs" className="hover:text-red-500 transition-colors" data-testid="header-docs-link">
              {t("nav_docs")}
            </Link>
            <div className="flex items-center gap-1 border border-neutral-700 rounded-sm overflow-hidden" data-testid="lang-toggle">
              <button
                onClick={() => setLang("tr")}
                className={`px-2 py-0.5 text-[10px] font-semibold ${lang === "tr" ? "bg-white text-black" : "text-neutral-400 hover:bg-neutral-900"}`}
                data-testid="lang-tr-btn"
              >
                TR
              </button>
              <button
                onClick={() => setLang("en")}
                className={`px-2 py-0.5 text-[10px] font-semibold ${lang === "en" ? "bg-white text-black" : "text-neutral-400 hover:bg-neutral-900"}`}
                data-testid="lang-en-btn"
              >
                EN
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main logo bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between gap-6">
        <button
          className="md:hidden text-neutral-100"
          onClick={() => setMobileOpen((s) => !s)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        <Link to="/" className="flex items-baseline gap-1 group" data-testid="logo-link">
          <span className="headline text-3xl sm:text-4xl tracking-tight text-white group-hover:text-red-500 transition-colors">
            loft
          </span>
          <span className="font-heading text-3xl sm:text-4xl font-light text-red-500">.</span>
          <span className="headline text-3xl sm:text-4xl tracking-tight text-white group-hover:text-red-500 transition-colors">
            press
          </span>
        </Link>

        <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-md mx-8 relative" data-testid="search-form">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("search_placeholder")}
            className="w-full bg-neutral-900 border border-neutral-800 rounded-sm pl-10 pr-3 py-2 text-sm text-neutral-100 font-body focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent placeholder:text-neutral-500"
            data-testid="header-search-input"
          />
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
        </form>

        <Link
          to="/marketplace"
          className="hidden md:inline-flex items-center bg-red-600 hover:bg-red-700 text-white text-xs font-bold tracking-wider uppercase px-4 py-2.5 rounded-sm transition-colors"
          data-testid="header-cta-get-api"
        >
          {t("cta_get_api")}
        </Link>

        {/* Auth area */}
        <div className="hidden md:flex items-center">
          {user ? (
            <div className="relative ml-3">
              <button
                onClick={(e) => { e.stopPropagation(); setUserMenuOpen((s) => !s); }}
                className="flex items-center gap-2 bg-neutral-900 hover:bg-neutral-800 border border-neutral-800 rounded-sm px-3 py-2 text-sm transition-colors"
                data-testid="header-user-btn"
              >
                <div className="w-7 h-7 bg-red-600 text-white text-xs font-bold flex items-center justify-center rounded-sm uppercase">
                  {(user.name || user.email)[0]}
                </div>
                <div className="hidden lg:flex flex-col items-start leading-tight">
                  <span className="text-white text-xs font-semibold truncate max-w-[120px]">{user.name || user.email.split("@")[0]}</span>
                  <span className="text-red-500 text-[10px] font-mono uppercase">{user.credits.toLocaleString()} {lang === "tr" ? "kredi" : "credits"}</span>
                </div>
                <ChevronDown size={14} className="text-neutral-500" />
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-neutral-950 border border-neutral-800 rounded-sm shadow-xl shadow-black/50 overflow-hidden z-50" data-testid="header-user-menu">
                  <div className="px-4 py-3 border-b border-neutral-800">
                    <p className="text-sm text-white font-semibold truncate">{user.name || "—"}</p>
                    <p className="text-xs text-neutral-500 truncate font-mono">{user.email}</p>
                  </div>
                  <div className="px-4 py-3 border-b border-neutral-800 bg-neutral-900/30">
                    <div className="flex items-center gap-2">
                      <Wallet size={14} className="text-red-500" />
                      <span className="text-xs uppercase tracking-wider text-neutral-500 font-bold">{lang === "tr" ? "Kredi" : "Credits"}</span>
                    </div>
                    <p className="mt-1 headline text-xl text-white">{user.credits.toLocaleString()}</p>
                  </div>
                  <Link to="/profile" className="flex items-center gap-2 px-4 py-2.5 text-sm text-neutral-300 hover:bg-neutral-900 hover:text-red-500 transition-colors" data-testid="menu-profile">
                    <User size={14} /> {lang === "tr" ? "Profilim" : "Profile"}
                  </Link>
                  <Link to="/pricing" className="flex items-center gap-2 px-4 py-2.5 text-sm text-neutral-300 hover:bg-neutral-900 hover:text-red-500 transition-colors" data-testid="menu-topup">
                    <Wallet size={14} /> {lang === "tr" ? "Kredi Ekle" : "Top Up"}
                  </Link>
                  {user.is_admin && (
                    <Link to="/admin" className="flex items-center gap-2 px-4 py-2.5 text-sm text-red-400 hover:bg-red-950/40 transition-colors border-t border-neutral-800" data-testid="menu-admin">
                      <span className="text-red-500">◆</span> {lang === "tr" ? "Admin Panel" : "Admin Panel"}
                    </Link>
                  )}
                  <button
                    onClick={() => { logout(); navigate("/"); }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-neutral-300 hover:bg-red-950/40 hover:text-red-400 transition-colors border-t border-neutral-800"
                    data-testid="menu-logout"
                  >
                    <LogOut size={14} /> {lang === "tr" ? "Çıkış Yap" : "Sign Out"}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              className="ml-3 inline-flex items-center gap-1 border border-neutral-700 hover:border-red-500 hover:text-red-500 text-white text-xs font-bold tracking-wider uppercase px-4 py-2.5 rounded-sm transition-colors"
              data-testid="header-login-btn"
            >
              <User size={14} /> {lang === "tr" ? "Giriş" : "Sign In"}
            </Link>
          )}
        </div>
      </div>

      {/* Category nav */}
      <nav className="border-y border-neutral-800 bg-black" data-testid="category-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-3">
          <div className="flex items-center overflow-x-auto scrollbar-hide gap-1 flex-1 min-w-0">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  isActive
                    ? "border-red-500 text-red-500"
                    : "border-transparent text-neutral-300 hover:text-red-500"
                }`
              }
              data-testid="nav-home"
            >
              {t("nav_home")}
            </NavLink>
            {CATEGORIES.map((c) => (
              <NavLink
                key={c.slug}
                to={`/category/${c.slug}`}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    isActive
                      ? "border-red-500 text-red-500"
                      : "border-transparent text-neutral-300 hover:text-red-500"
                  }`
                }
                data-testid={`nav-cat-${c.slug}`}
              >
                <span aria-hidden>{c.emoji}</span>
                <span>{c[lang]}</span>
              </NavLink>
            ))}
          </div>

          {/* Faz: API scope notice (desktop inline) */}
          {!noticeDismissed && (
          <div className="hidden lg:flex flex-shrink-0 items-stretch" data-testid="api-scope-notice-desktop-wrap">
            <Link
              to="/marketplace"
              className="inline-flex items-center gap-2 border border-red-600 border-r-0 bg-neutral-950 hover:bg-neutral-900 rounded-l-sm pl-3 pr-2 py-1.5 text-xs transition-colors group"
              data-testid="api-scope-notice-desktop"
            >
              <ShieldAlert size={13} className="text-red-500 flex-shrink-0" aria-hidden />
              <span className="text-white">
                {lang === "tr"
                  ? "Sitemizdeki haberler API olarak sunulmaz."
                  : "News on our website is not offered as an API."}
              </span>
              <span className="inline-flex items-center gap-1 text-red-500 font-bold whitespace-nowrap group-hover:text-red-400">
                {lang === "tr" ? "Detaylı Bilgi" : "Learn More"}
                <ArrowRight size={12} />
              </span>
            </Link>
            <button
              type="button"
              onClick={dismissNotice}
              aria-label={lang === "tr" ? "Bilgilendirmeyi kapat" : "Dismiss notice"}
              className="inline-flex items-center justify-center border-y border-r border-l border-red-600 bg-neutral-950 hover:bg-neutral-900 rounded-r-sm px-2 text-neutral-400 hover:text-white transition-colors"
              data-testid="api-scope-notice-close-desktop"
            >
              <X size={13} />
            </button>
          </div>
          )}
        </div>

        {/* Mobile / tablet variant — full-width bar below category strip */}
        {!noticeDismissed && (
        <div className="lg:hidden flex items-start gap-2 border-t border-red-900/60 bg-neutral-950 px-4 py-2 text-xs" data-testid="api-scope-notice-mobile-wrap">
          <ShieldAlert size={13} className="text-red-500 flex-shrink-0 mt-0.5" aria-hidden />
          <Link
            to="/marketplace"
            className="flex-1 text-white leading-snug hover:opacity-90"
            data-testid="api-scope-notice-mobile"
          >
            {lang === "tr"
              ? "Sitemizdeki haberler API olarak sunulmaz."
              : "News on our website is not offered as an API."}
            {" "}
            <span className="inline-flex items-center gap-1 text-red-500 font-bold whitespace-nowrap">
              {lang === "tr" ? "Detaylı Bilgi" : "Learn More"}
              <ArrowRight size={11} />
            </span>
          </Link>
          <button
            type="button"
            onClick={dismissNotice}
            aria-label={lang === "tr" ? "Bilgilendirmeyi kapat" : "Dismiss notice"}
            className="flex-shrink-0 -mt-0.5 p-1 text-neutral-400 hover:text-white"
            data-testid="api-scope-notice-close-mobile"
          >
            <X size={14} />
          </button>
        </div>
        )}
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-b border-neutral-800 bg-black px-4 py-4 space-y-3" data-testid="mobile-menu">
          <form onSubmit={onSearch} className="relative">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("search_placeholder")}
              className="w-full bg-neutral-900 border border-neutral-800 rounded-sm pl-10 pr-3 py-2 text-sm text-neutral-100 placeholder:text-neutral-500"
              data-testid="mobile-search-input"
            />
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          </form>
          <div className="flex items-center justify-between">
            <Link
              to="/marketplace"
              onClick={() => setMobileOpen(false)}
              className="inline-flex items-center bg-red-600 text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-sm"
              data-testid="mobile-cta-get-api"
            >
              {t("cta_get_api")}
            </Link>
            <div className="flex items-center gap-1 border border-neutral-700 rounded-sm overflow-hidden">
              <button onClick={() => setLang("tr")} className={`px-2 py-1 text-xs font-semibold ${lang === "tr" ? "bg-white text-black" : "text-neutral-400"}`} data-testid="mobile-lang-tr">TR</button>
              <button onClick={() => setLang("en")} className={`px-2 py-1 text-xs font-semibold ${lang === "en" ? "bg-white text-black" : "text-neutral-400"}`} data-testid="mobile-lang-en">EN</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
